import { createContext, useState, useCallback, useMemo } from 'react'
import Google from '~/lib/google'
import { captureException } from '~/lib/sentry/captureException'

export const GoogleContext = createContext(null)

const GoogleProvider = ({ children, clientId }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [google, setGoogle] = useState(null)

  const isAvailable = Boolean(clientId)

  const initialize = useCallback(() => {
    if (!clientId) return null

    return new Promise(resolve => {
      if (isInitialized) {
        return resolve(google)
      }

      if (window.gapi) {
        const google = new Google(window.gapi.auth2.getAuthInstance())
        setGoogle(google)
        setIsInitialized(true)
        return resolve(google)
      }

      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.defer = true
      script.crossorigin = 'anonymous'
      script.src = 'https://apis.google.com/js/platform.js'
      document.body.appendChild(script)

      script.onload = () => {
        window.gapi.load('auth2', () => {
          window.gapi.auth2
            .init({
              clientId,
              scope: 'profile email',
            })
            .then(() => {
              const google = new Google(window.gapi.auth2.getAuthInstance())
              setGoogle(google)
              setIsInitialized(true)
              resolve(google)
            })
            .catch(error => {
              console.error(error)
              captureException(error, 'Google - login')
            })
        })
      }
    })
  }, [clientId, google, isInitialized])

  const isLoggedIn = useCallback(async () => {
    const google = await initialize()

    return google.isLoggedIn()
  }, [initialize])

  const login = useCallback(async () => {
    const google = await initialize()

    return new Promise(async (resolve, reject) => {
      try {
        const response = await google.login()
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  }, [initialize])

  const logout = useCallback(async () => {
    const google = await initialize()

    return new Promise(async resolve => {
      try {
        google.logout()
      } catch {}

      resolve()
    })
  }, [initialize])

  const value = useMemo(() => {
    return {
      initialize,
      isInitialized,
      isLoggedIn,
      login,
      logout,
      isAvailable,
    }
  }, [initialize, isInitialized, isLoggedIn, login, logout, isAvailable])

  return <GoogleContext.Provider value={value}>{children}</GoogleContext.Provider>
}

export default GoogleProvider
